/**
 * ActivityPanel.js
 * Bottom sliding card panel for quick activity editing
 * Opens when an activity is selected, does NOT block gantt visibility
 * 
 * Features:
 * - Detailed activity history with field-level changes
 * - Optional comments per change
 * - Session-level undo support
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import {
  X, ChevronDown, ChevronUp, Calendar as CalendarIcon,
  Users, Clock, History, Edit3, Save, Loader2,
  CheckCircle2, AlertCircle, Boxes, Undo2, MessageSquare,
  ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/api';

// Maximum undo stack size
const MAX_UNDO_STACK = 20;

const ActivityPanel = ({
  activity,
  onClose,
  onActivityUpdate,
  resources = [],
  calendars = [],
  scheduleId,
  canEdit = false,
  undoStack = [],
  setUndoStack
}) => {
  // Panel state
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  // Edit states
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Optional comment for changes
  const [changeComment, setChangeComment] = useState('');
  const [showCommentInput, setShowCommentInput] = useState(false);
  
  // Date edit states
  const [editingField, setEditingField] = useState(null);
  const [tempDateValue, setTempDateValue] = useState(null);
  
  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Resource assignment state
  const [selectedResourceIds, setSelectedResourceIds] = useState([]);
  const [isAssigningResources, setIsAssigningResources] = useState(false);
  
  // Undo state
  const [isUndoing, setIsUndoing] = useState(false);

  // Initialize edit name when activity changes
  useEffect(() => {
    if (activity) {
      setEditName(activity.name || '');
      setSelectedResourceIds(activity.resource_ids || []);
      setChangeComment('');
      setShowCommentInput(false);
    }
  }, [activity?.id]);

  // Fetch activity history using the new dedicated endpoint
  const fetchHistory = useCallback(async () => {
    if (!activity?.id) return;
    
    setLoadingHistory(true);
    try {
      const response = await api.get(`/history/activity/${activity.id}`, {
        params: { page_size: 50 }
      });
      setHistory(response.data?.entries || []);
    } catch (error) {
      console.error('Failed to fetch activity history:', error);
      // Fallback to schedule history with filter
      try {
        const fallbackResponse = await api.get(`/history/schedule/${scheduleId}`, {
          params: { entity_type: 'activity', page_size: 50 }
        });
        const filtered = (fallbackResponse.data?.entries || []).filter(
          entry => entry.entity_id === activity.id
        );
        setHistory(filtered);
      } catch {
        setHistory([]);
      }
    } finally {
      setLoadingHistory(false);
    }
  }, [activity?.id, scheduleId]);

  // Load history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  // Push to undo stack
  const pushToUndoStack = (changeData) => {
    if (!setUndoStack) return;
    
    const newEntry = {
      id: Date.now(),
      activityId: activity.id,
      activityName: activity.name,
      timestamp: new Date().toISOString(),
      ...changeData
    };
    
    setUndoStack(prev => {
      const newStack = [newEntry, ...prev];
      return newStack.slice(0, MAX_UNDO_STACK);
    });
  };

  // Handle undo
  const handleUndo = async () => {
    if (!undoStack || undoStack.length === 0) return;
    
    const lastChange = undoStack[0];
    
    // Only undo changes for the current activity
    if (lastChange.activityId !== activity.id) {
      toast.error('Latest change is for a different activity');
      return;
    }
    
    setIsUndoing(true);
    try {
      // Build undo data (revert to previous value)
      const undoData = {
        [lastChange.field]: lastChange.oldValue,
        comment: `Undo: ${lastChange.displayName} reverted from "${lastChange.newValue}" to "${lastChange.oldValue}"`
      };
      
      await api.put(`/activities/${activity.id}`, undoData);
      
      // Remove from undo stack
      setUndoStack(prev => prev.slice(1));
      
      toast.success(`Undone: ${lastChange.displayName}`);
      onActivityUpdate?.();
      
      // Refresh history
      if (activeTab === 'history') {
        fetchHistory();
      }
    } catch (error) {
      console.error('Failed to undo:', error);
      toast.error('Failed to undo change');
    } finally {
      setIsUndoing(false);
    }
  };

  // Handle activity update with undo tracking
  const handleUpdateWithUndo = async (field, newValue, displayName, oldValue) => {
    setIsSaving(true);
    try {
      const updateData = {
        [field]: newValue,
        comment: changeComment || null
      };
      
      await api.put(`/activities/${activity.id}`, updateData);
      
      // Push to undo stack
      pushToUndoStack({
        field,
        displayName,
        oldValue,
        newValue
      });
      
      toast.success(`${displayName} updated`);
      onActivityUpdate?.();
      setChangeComment('');
      setShowCommentInput(false);
      
      // Refresh history if on that tab
      if (activeTab === 'history') {
        fetchHistory();
      }
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error(`Failed to update ${displayName}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle name save
  const handleSaveName = async () => {
    if (!editName.trim() || editName === activity.name) {
      setIsEditingName(false);
      return;
    }
    
    await handleUpdateWithUndo('name', editName.trim(), 'Activity Name', activity.name);
    setIsEditingName(false);
  };

  // Handle date field save
  const handleSaveDate = async (field, value) => {
    if (!value) return;
    
    const fieldMap = {
      'planned_start': { api: 'planned_start', display: 'Start', getter: () => activity.planned_start || activity.start_date },
      'planned_finish': { api: 'planned_finish', display: 'Finish', getter: () => activity.planned_finish || activity.finish_date },
      'actual_start': { api: 'actual_start', display: 'Actual Start', getter: () => activity.actual_start },
      'actual_finish': { api: 'actual_finish', display: 'Actual Finish', getter: () => activity.actual_finish },
      'expected_finish': { api: 'expected_finish', display: 'Expected Finish', getter: () => activity.expected_finish }
    };
    
    const config = fieldMap[field];
    if (!config) return;
    
    const oldValue = config.getter();
    const newValue = format(value, 'yyyy-MM-dd');
    
    await handleUpdateWithUndo(config.api, newValue, config.display, oldValue);
    setEditingField(null);
    setTempDateValue(null);
  };

  // Handle duration save
  const handleSaveDuration = async (newDuration) => {
    if (!newDuration || isNaN(parseInt(newDuration))) return;
    
    const oldValue = activity.original_duration || activity.duration || 0;
    await handleUpdateWithUndo('original_duration', parseInt(newDuration), 'Duration', oldValue);
    setEditingField(null);
  };

  // Handle progress save
  const handleSaveProgress = async (newProgress) => {
    const progress = parseInt(newProgress);
    if (isNaN(progress) || progress < 0 || progress > 100) return;
    
    const oldValue = activity.percent_complete || 0;
    await handleUpdateWithUndo('percent_complete', progress, 'Progress', oldValue);
    setEditingField(null);
  };

  // Handle calendar change
  const handleCalendarChange = async (calendarId) => {
    const oldValue = activity.calendar_id || 'default';
    const newValue = calendarId === 'default' ? null : calendarId;
    
    await handleUpdateWithUndo('calendar_id', newValue, 'Calendar', oldValue);
  };

  // Handle resource assignment
  const handleSaveResources = async () => {
    setIsAssigningResources(true);
    try {
      const updateData = {
        resource_ids: selectedResourceIds,
        comment: changeComment || null
      };
      
      await api.put(`/activities/${activity.id}`, updateData);
      
      // Push to undo stack
      pushToUndoStack({
        field: 'resource_ids',
        displayName: 'Resources',
        oldValue: activity.resource_ids || [],
        newValue: selectedResourceIds
      });
      
      toast.success('Resources updated');
      onActivityUpdate?.();
      setChangeComment('');
      setShowCommentInput(false);
      
      if (activeTab === 'history') {
        fetchHistory();
      }
    } catch (error) {
      console.error('Failed to update resources:', error);
      toast.error('Failed to update resources');
    } finally {
      setIsAssigningResources(false);
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return '—';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: 'Completed', className: 'bg-green-100 text-green-700' },
      in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
      not_started: { label: 'Not Started', className: 'bg-slate-100 text-slate-700' }
    };
    const config = statusConfig[status] || statusConfig.not_started;
    return <Badge className={cn('text-xs', config.className)}>{config.label}</Badge>;
  };

  // Date picker component
  const DateField = ({ label, field, value, isActual = false }) => {
    const isEditing = editingField === field;
    const dateValue = value ? parseISO(value) : undefined;

    if (!canEdit) {
      return (
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">{label}</Label>
          <div className="text-sm font-medium">{formatDate(value)}</div>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{label}</Label>
        <Popover open={isEditing} onOpenChange={(open) => {
          if (!open) {
            setEditingField(null);
            setTempDateValue(null);
          }
        }}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "w-full h-8 justify-start text-left font-normal text-sm",
                !value && "text-slate-400",
                isActual && "border-green-200 bg-green-50/50"
              )}
              onClick={() => {
                setEditingField(field);
                setTempDateValue(dateValue || null);
              }}
            >
              <CalendarIcon className="mr-2 h-3.5 w-3.5" />
              {formatDate(value)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={tempDateValue || dateValue}
              onSelect={(date) => setTempDateValue(date)}
              initialFocus
            />
            <div className="p-2 border-t space-y-2">
              {/* Optional comment input */}
              <Input
                placeholder="Add comment (optional)"
                value={changeComment}
                onChange={(e) => setChangeComment(e.target.value)}
                className="h-8 text-xs"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingField(null);
                    setTempDateValue(null);
                    setChangeComment('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={isSaving || !tempDateValue}
                  onClick={() => handleSaveDate(field, tempDateValue)}
                >
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  // Check if there are undoable changes for this activity
  const hasUndoableChanges = undoStack && undoStack.length > 0 && undoStack[0]?.activityId === activity?.id;

  if (!activity) return null;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-2xl transition-all duration-300 ease-out z-40",
        isMinimized ? "h-12" : "h-[360px]"
      )}
      data-testid="activity-panel"
    >
      {/* Panel Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100 bg-slate-50/80">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Badge variant="outline" className="font-mono text-xs shrink-0">
            {activity.activity_id}
          </Badge>
          
          {/* Editable Name */}
          {isEditingName ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-7 text-sm font-medium max-w-xs"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') {
                    setEditName(activity.name);
                    setIsEditingName(false);
                  }
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={handleSaveName}
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5 text-green-600" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => {
                  setEditName(activity.name);
                  setIsEditingName(false);
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="font-medium text-sm truncate">{activity.name}</span>
              {canEdit && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 shrink-0"
                  onClick={() => setIsEditingName(true)}
                >
                  <Edit3 className="h-3 w-3 text-slate-400" />
                </Button>
              )}
            </div>
          )}
          
          {getStatusBadge(activity.status)}
        </div>

        <div className="flex items-center gap-1">
          {/* Undo Button */}
          {canEdit && hasUndoableChanges && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 gap-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
              onClick={handleUndo}
              disabled={isUndoing}
              title={`Undo: ${undoStack[0]?.displayName}`}
            >
              {isUndoing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Undo2 className="h-3.5 w-3.5" />
              )}
              <span className="text-xs hidden sm:inline">Undo</span>
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Panel Content */}
      {!isMinimized && (
        <div className="h-[calc(100%-48px)] overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <div className="px-4 border-b border-slate-100">
              <TabsList className="h-9 bg-transparent p-0 gap-4">
                <TabsTrigger 
                  value="details" 
                  className="px-0 pb-2 h-9 data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none bg-transparent data-[state=active]:shadow-none text-xs"
                >
                  <Clock className="h-3.5 w-3.5 mr-1.5" />
                  Dates & Progress
                </TabsTrigger>
                <TabsTrigger 
                  value="resources" 
                  className="px-0 pb-2 h-9 data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none bg-transparent data-[state=active]:shadow-none text-xs"
                >
                  <Users className="h-3.5 w-3.5 mr-1.5" />
                  Resources
                </TabsTrigger>
                <TabsTrigger 
                  value="history" 
                  className="px-0 pb-2 h-9 data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none bg-transparent data-[state=active]:shadow-none text-xs"
                >
                  <History className="h-3.5 w-3.5 mr-1.5" />
                  History
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Details Tab */}
            <TabsContent value="details" className="flex-1 m-0 overflow-auto">
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Current Dates Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Current Dates</h4>
                  <DateField label="Start" field="planned_start" value={activity.planned_start || activity.start_date} />
                  <DateField label="Finish" field="planned_finish" value={activity.planned_finish || activity.finish_date} />
                </div>

                {/* Actual Dates Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Actual Dates</h4>
                  <DateField label="Actual Start" field="actual_start" value={activity.actual_start} isActual />
                  <DateField label="Actual Finish" field="actual_finish" value={activity.actual_finish} isActual />
                </div>

                {/* Duration & Progress */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Duration & Progress</h4>
                  
                  {/* Duration */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Duration (days)</Label>
                    {canEdit && editingField === 'duration' ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            defaultValue={activity.original_duration || activity.duration || 0}
                            className="h-8 w-20 text-sm"
                            autoFocus
                            id="duration-input"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveDuration(e.target.value);
                              }
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                          />
                        </div>
                        <Input
                          placeholder="Add comment (optional)"
                          value={changeComment}
                          onChange={(e) => setChangeComment(e.target.value)}
                          className="h-7 text-xs"
                        />
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => {
                              setEditingField(null);
                              setChangeComment('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              const input = document.getElementById('duration-input');
                              handleSaveDuration(input?.value);
                            }}
                            disabled={isSaving}
                          >
                            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={cn(
                          "text-sm font-medium",
                          canEdit && "cursor-pointer hover:text-blue-600"
                        )}
                        onClick={() => canEdit && setEditingField('duration')}
                      >
                        {activity.original_duration || activity.duration || 0} days
                      </div>
                    )}
                  </div>

                  {/* Progress */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Progress</Label>
                    {canEdit && editingField === 'progress' ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            defaultValue={activity.percent_complete || 0}
                            className="h-8 w-20 text-sm"
                            autoFocus
                            id="progress-input"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveProgress(e.target.value);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                        <Input
                          placeholder="Add comment (optional)"
                          value={changeComment}
                          onChange={(e) => setChangeComment(e.target.value)}
                          className="h-7 text-xs"
                        />
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => {
                              setEditingField(null);
                              setChangeComment('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              const input = document.getElementById('progress-input');
                              handleSaveProgress(input?.value);
                            }}
                            disabled={isSaving}
                          >
                            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[100px]">
                          <div 
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${activity.percent_complete || 0}%` }}
                          />
                        </div>
                        <span 
                          className={cn(
                            "text-sm font-medium",
                            canEdit && "cursor-pointer hover:text-blue-600"
                          )}
                          onClick={() => canEdit && setEditingField('progress')}
                        >
                          {activity.percent_complete || 0}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expected & Calendar */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Forecast & Calendar</h4>
                  <DateField 
                    label="Expected Finish" 
                    field="expected_finish" 
                    value={activity.expected_finish}
                  />
                  
                  {/* Calendar Selection */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Calendar</Label>
                    {canEdit ? (
                      <Select
                        value={activity.calendar_id || 'default'}
                        onValueChange={handleCalendarChange}
                        disabled={isSaving}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Select calendar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">
                            <span className="text-slate-500">Project Default</span>
                          </SelectItem>
                          {calendars.map((cal) => (
                            <SelectItem key={cal.id} value={cal.id}>
                              {cal.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-sm font-medium">
                        {calendars.find(c => c.id === activity.calendar_id)?.name || 'Project Default'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Resources Tab */}
            <TabsContent value="resources" className="flex-1 m-0 overflow-auto">
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Assigned Resources */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Assigned Resources</h4>
                    
                    {selectedResourceIds.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">No resources assigned</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectedResourceIds.map((resourceId) => {
                          const resource = resources.find(r => r.id === resourceId);
                          return resource ? (
                            <Badge 
                              key={resourceId} 
                              variant="secondary"
                              className="flex items-center gap-1.5 pr-1"
                            >
                              <Boxes className="h-3 w-3" />
                              <span>{resource.name}</span>
                              {canEdit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 ml-1 hover:bg-slate-300"
                                  onClick={() => {
                                    setSelectedResourceIds(prev => 
                                      prev.filter(id => id !== resourceId)
                                    );
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              )}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>

                  {/* Add Resources */}
                  {canEdit && (
                    <div className="space-y-3">
                      <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Add Resources</h4>
                      <div className="space-y-2">
                        <Select
                          onValueChange={(value) => {
                            if (!selectedResourceIds.includes(value)) {
                              setSelectedResourceIds(prev => [...prev, value]);
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Select resource to add" />
                          </SelectTrigger>
                          <SelectContent>
                            {resources
                              .filter(r => !selectedResourceIds.includes(r.id))
                              .map((resource) => (
                                <SelectItem key={resource.id} value={resource.id}>
                                  <div className="flex items-center gap-2">
                                    <Boxes className="h-3 w-3" />
                                    {resource.name}
                                  </div>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Show save button if resources changed */}
                        {JSON.stringify(selectedResourceIds.sort()) !== 
                         JSON.stringify((activity.resource_ids || []).sort()) && (
                          <div className="space-y-2">
                            <Input
                              placeholder="Add comment (optional)"
                              value={changeComment}
                              onChange={(e) => setChangeComment(e.target.value)}
                              className="h-8 text-xs"
                            />
                            <Button
                              size="sm"
                              onClick={handleSaveResources}
                              disabled={isAssigningResources}
                              className="w-full"
                            >
                              {isAssigningResources ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-2" />
                              ) : (
                                <Save className="h-3 w-3 mr-2" />
                              )}
                              Save Resource Changes
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* History Tab - Enhanced with field-level changes */}
            <TabsContent value="history" className="flex-1 m-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Change History</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={fetchHistory}
                      disabled={loadingHistory}
                    >
                      {loadingHistory ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        'Refresh'
                      )}
                    </Button>
                  </div>
                  
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  ) : history.length === 0 ? (
                    <p className="text-sm text-slate-400 italic py-4">No history available</p>
                  ) : (
                    <div className="space-y-3">
                      {history.map((entry) => (
                        <div 
                          key={entry.id} 
                          className="p-3 rounded-lg bg-slate-50 border border-slate-100"
                        >
                          {/* Header */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-slate-600">
                                {entry.user_name}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                {entry.timestamp ? format(parseISO(entry.timestamp), 'MMM d, yyyy h:mm a') : ''}
                              </span>
                            </div>
                          </div>
                          
                          {/* Field Changes */}
                          {entry.field_changes && entry.field_changes.length > 0 ? (
                            <div className="space-y-1.5">
                              {entry.field_changes.map((change, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs bg-white p-2 rounded border border-slate-100">
                                  <span className="font-medium text-slate-700 min-w-[100px]">
                                    {change.display_name || change.field}
                                  </span>
                                  <span className="text-red-500 line-through">
                                    {change.old_value !== null && change.old_value !== undefined 
                                      ? String(change.old_value) 
                                      : '(empty)'}
                                  </span>
                                  <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                                  <span className="text-green-600 font-medium">
                                    {change.new_value !== null && change.new_value !== undefined 
                                      ? String(change.new_value) 
                                      : '(empty)'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            /* Legacy format - before/after values */
                            entry.before_value && entry.after_value && (
                              <div className="text-xs bg-white p-2 rounded border border-slate-100">
                                <span className="text-red-500 line-through mr-2">
                                  {typeof entry.before_value === 'object' 
                                    ? JSON.stringify(entry.before_value) 
                                    : entry.before_value}
                                </span>
                                <ArrowRight className="h-3 w-3 text-slate-400 inline mx-1" />
                                <span className="text-green-600 ml-2">
                                  {typeof entry.after_value === 'object' 
                                    ? JSON.stringify(entry.after_value) 
                                    : entry.after_value}
                                </span>
                              </div>
                            )
                          )}
                          
                          {/* Comment */}
                          {entry.comment && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-600 bg-amber-50 p-2 rounded border border-amber-100">
                              <MessageSquare className="h-3 w-3 mt-0.5 text-amber-500 shrink-0" />
                              <span>{entry.comment}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default ActivityPanel;
